// src/pages/LogsView.tsx - Compact Activity Logs with Pagination & Bulk Delete
import Select from '@/components/common/Select.tsx';
import { supabase } from '@/lib/supabase';
import type { ActivityLog, Profile } from '@/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    ChevronDown,
    ChevronUp,
    Eye,
    Filter,
    Search,
    Trash2,
    X
} from 'lucide-react';
import { useState, type ChangeEvent } from 'react';

interface LogWithUser extends ActivityLog {
    user?: Profile
}

export default function LogsView() {
    const queryClient = useQueryClient();
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [filterAction, setFilterAction] = useState('');
    const [filterUserId, setFilterUserId] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Selection & Modals
    const [selectedLogs, setSelectedLogs] = useState<Set<string>>(new Set());
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [detailLog, setDetailLog] = useState<LogWithUser | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Fetch logs WITHOUT user join
    const { data: rawLogs = [], isLoading: loadingLogs } = useQuery({
        queryKey: ['activity-logs', filterCategory, filterAction, filterUserId, dateFrom, dateTo],
        queryFn: async () => {
            let query = supabase
                .from('activity_logs')
                .select('*')
                .order('created_at', { ascending: false });

            if (filterCategory) query = query.eq('action_category', filterCategory);
            if (filterAction) query = query.eq('action_type', filterAction);
            if (filterUserId) query = query.eq('user_id', filterUserId);
            if (dateFrom) query = query.gte('created_at', `${dateFrom}T00:00:00Z`);
            if (dateTo) query = query.lte('created_at', `${dateTo}T23:59:59Z`);

            const { data, error } = await query;
            if (error) throw error;
            return data as ActivityLog[];
        }
    });

    // Fetch users
    const { data: users = [] } = useQuery({
        queryKey: ['users-for-logs'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('profiles')
                .select('id, email, first_name, last_name')
                .order('email');
            if (error) throw error;
            return data as Profile[];
        }
    });

    // Match logs with users
    const logs: LogWithUser[] = rawLogs.map(log => ({
        ...log,
        user: users.find(u => u.id === log.user_id)
    }));

    // Get full name helper
    const getUserFullName = (user?: Profile): string => {
        if (!user) return 'Sconosciuto';
        const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
        return fullName || user.email || 'Sconosciuto';
    };

    // Filter by search
    const filteredLogs = logs.filter(log => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        const userFullName = getUserFullName(log.user).toLowerCase();
        return (
            log.description?.toLowerCase().includes(query) ||
            log.entity_type?.toLowerCase().includes(query) ||
            userFullName.includes(query)
        );
    });

    // Pagination
    const totalPages = Math.ceil(filteredLogs.length / pageSize);
    const startIdx = (currentPage - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    const paginatedLogs = filteredLogs.slice(startIdx, endIdx);

    // Reset page when filters change
    const handleFilterChange = () => {
        setCurrentPage(1);
        setSelectedLogs(new Set());
    };

    // Format timestamp
    const formatTimestamp = (timestamp: string): string => {
        const date = new Date(timestamp);
        return date.toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Selection handlers
    const toggleSelectAll = () => {
        if (selectedLogs.size === paginatedLogs.length) {
            setSelectedLogs(new Set());
        } else {
            setSelectedLogs(new Set(paginatedLogs.map(log => log.id)));
        }
    };

    const toggleSelect = (logId: string) => {
        const newSelected = new Set(selectedLogs);
        if (newSelected.has(logId)) {
            newSelected.delete(logId);
        } else {
            newSelected.add(logId);
        }
        setSelectedLogs(newSelected);
    };

    // Delete mutation - FIXED: properly invalidate all activity-logs queries
    const deleteLogs = useMutation({
        mutationFn: async (logIds: string[]) => {
            console.log('🗑️ DELETE: Starting delete for IDs:', logIds);
            const { data, error } = await supabase
                .from('activity_logs')
                .delete()
                .in('id', logIds)
                .select();

            console.log('🗑️ DELETE: Response data:', data);
            console.log('🗑️ DELETE: Response error:', error);

            if (error) {
                console.error('🗑️ DELETE: Error occurred:', error);
                throw error;
            }

            console.log('🗑️ DELETE: Success! Deleted', data?.length, 'records');
            return data;
        },
        onSuccess: () => {
            console.log('🗑️ DELETE: onSuccess called, invalidating queries');
            // FIX: Invalidate all queries starting with 'activity-logs' to catch filtered queries
            queryClient.invalidateQueries({ queryKey: ['activity-logs'] });
            setSelectedLogs(new Set());
            setShowDeleteConfirm(false);
            console.log('🗑️ DELETE: Complete!');
        },
        onError: (error: Error) => {
            console.error('🗑️ DELETE: onError called:', error);
            alert(`Errore: ${error.message}`);
        }
    });

    const handleDelete = () => {
        console.log('🗑️ DELETE: handleDelete called');
        console.log('🗑️ DELETE: Selected logs:', Array.from(selectedLogs));
        console.log('🗑️ DELETE: Mutation pending?', deleteLogs.isPending);
        deleteLogs.mutate(Array.from(selectedLogs));
    };

    const clearFilters = () => {
        setFilterCategory('');
        setFilterAction('');
        setFilterUserId('');
        setDateFrom('');
        setDateTo('');
        setSearchQuery('');
        handleFilterChange();
    };

    const hasActiveFilters = filterCategory || filterAction || filterUserId || dateFrom || dateTo || searchQuery;

    if (loadingLogs) {
        return (
            <div className="p-4 md:p-8 min-h-screen bg-slate-50">
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 min-h-screen bg-slate-50">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h1 className="text-3xl font-bold text-slate-800 mb-2">Log Attività</h1>
                            <p className="text-slate-600">Cronologia delle operazioni</p>
                        </div>
                        <div className="flex gap-2">
                            {selectedLogs.size > 0 && (
                                <button
                                    onClick={() => setShowDeleteConfirm(true)}
                                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Elimina ({selectedLogs.size})
                                </button>
                            )}
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${showFilters || hasActiveFilters
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
                                    }`}
                            >
                                <Filter className="w-4 h-4" />
                                <span className="hidden sm:inline">Filtri</span>
                            </button>
                        </div>
                    </div>

                    {/* Search */}
                    <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                setSearchQuery(e.target.value);
                                handleFilterChange();
                            }}
                            placeholder="Cerca..."
                            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    {/* Filters Panel */}
                    {showFilters && (
                        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 mb-4">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-semibold text-slate-800">Filtri</h3>
                                {hasActiveFilters && (
                                    <button onClick={clearFilters} className="text-sm text-blue-600 hover:text-blue-700">
                                        Cancella tutti
                                    </button>
                                )}
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                <Select
                                    value={filterCategory}
                                    onChange={(e) => { setFilterCategory(e.target.value); handleFilterChange(); }}
                                    options={[
                                        { value: '', label: 'Tutte le categorie' },
                                        { value: 'auth', label: 'Autenticazione' },
                                        { value: 'article', label: 'Articoli' },
                                        { value: 'category', label: 'Categorie' },
                                        { value: 'location', label: 'Posizioni' }
                                    ]}
                                />
                                <Select
                                    value={filterAction}
                                    onChange={(e) => { setFilterAction(e.target.value); handleFilterChange(); }}
                                    options={[
                                        { value: '', label: 'Tutte le azioni' },
                                        { value: 'created', label: 'Creazione' },
                                        { value: 'updated', label: 'Modifica' },
                                        { value: 'deleted', label: 'Eliminazione' },
                                        { value: 'login', label: 'Login' }
                                    ]}
                                />
                                <Select
                                    value={filterUserId}
                                    onChange={(e) => { setFilterUserId(e.target.value); handleFilterChange(); }}
                                    options={[
                                        { value: '', label: 'Tutti gli utenti' },
                                        ...users.map(user => ({ value: user.id, label: getUserFullName(user) }))
                                    ]}
                                />
                                <input
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => { setDateFrom(e.target.value); handleFilterChange(); }}
                                    className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <input
                                    type="date"
                                    value={dateTo}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => { setDateTo(e.target.value); handleFilterChange(); }}
                                    className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Compact Table */}
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                    {/* Header */}
                    <div className="border-b border-slate-200 bg-slate-50">
                        <div className="grid grid-cols-12 gap-2 p-3 text-xs font-semibold text-slate-600">
                            <div className="col-span-1 flex items-center">
                                <input
                                    type="checkbox"
                                    checked={selectedLogs.size === paginatedLogs.length && paginatedLogs.length > 0}
                                    onChange={toggleSelectAll}
                                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div className="col-span-2">Utente</div>
                            <div className="col-span-2 hidden sm:block">Data</div>
                            <div className="col-span-6 sm:col-span-6">Descrizione</div>
                            <div className="col-span-1"></div>
                        </div>
                    </div>

                    {/* Rows */}
                    <div className="divide-y divide-slate-200">
                        {paginatedLogs.length === 0 ? (
                            <div className="p-12 text-center text-slate-500">
                                Nessun log trovato
                            </div>
                        ) : (
                            paginatedLogs.map(log => (
                                <div
                                    key={log.id}
                                    className={`grid grid-cols-12 gap-2 p-3 hover:bg-slate-50 transition-colors ${selectedLogs.has(log.id) ? 'bg-blue-50' : ''
                                        }`}
                                >
                                    <div className="col-span-1 flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={selectedLogs.has(log.id)}
                                            onChange={() => toggleSelect(log.id)}
                                            className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div className="col-span-2 text-sm truncate">
                                        {getUserFullName(log.user)}
                                    </div>
                                    <div className="col-span-2 hidden sm:block text-sm text-slate-500">
                                        {formatTimestamp(log.created_at!)}
                                    </div>
                                    <div className="col-span-6 sm:col-span-6">
                                        <p className="text-sm truncate">{log.description}</p>
                                        <div className="sm:hidden text-xs text-slate-500 mt-0.5">
                                            {formatTimestamp(log.created_at!)}
                                        </div>
                                    </div>
                                    <div className="col-span-1 flex items-center justify-end">
                                        <button
                                            onClick={() => {
                                                setDetailLog(log);
                                                setShowDetailModal(true);
                                            }}
                                            className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors"
                                            title="Dettagli"
                                        >
                                            <Eye className="w-4 h-4 text-slate-600" />
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Pagination Footer */}
                    {filteredLogs.length > 0 && (
                        <div className="border-t border-slate-200 bg-slate-50 p-3">
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                                <div className="flex items-center gap-2 text-sm text-slate-600">
                                    <span>Mostra</span>
                                    <Select
                                        value={pageSize.toString()}
                                        onChange={(e) => {
                                            setPageSize(Number(e.target.value));
                                            setCurrentPage(1);
                                            setSelectedLogs(new Set());
                                        }}
                                        options={[
                                            { value: '10', label: '10' },
                                            { value: '20', label: '20' },
                                            { value: '50', label: '50' },
                                            { value: '100', label: '100' }
                                        ]}
                                        className="w-20"
                                    />
                                    <span>
                                        {startIdx + 1}-{Math.min(endIdx, filteredLogs.length)} di {filteredLogs.length}
                                    </span>
                                </div>

                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => setCurrentPage(1)}
                                        disabled={currentPage === 1}
                                        className="px-3 py-1 text-sm rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Prima
                                    </button>
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        className="p-1 rounded hover:bg-slate-200 disabled:opacity-50"
                                    >
                                        <ChevronDown className="w-4 h-4 rotate-90" />
                                    </button>
                                    <span className="px-3 py-1 text-sm">
                                        {currentPage} / {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage === totalPages}
                                        className="p-1 rounded hover:bg-slate-200 disabled:opacity-50"
                                    >
                                        <ChevronUp className="w-4 h-4 rotate-90" />
                                    </button>
                                    <button
                                        onClick={() => setCurrentPage(totalPages)}
                                        disabled={currentPage === totalPages}
                                        className="px-3 py-1 text-sm rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Ultima
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Detail Modal */}
                {showDetailModal && detailLog && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
                            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                                <h3 className="text-lg font-semibold">Dettagli Log</h3>
                                <button
                                    onClick={() => setShowDetailModal(false)}
                                    className="p-1 hover:bg-slate-100 rounded"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
                                <div className="space-y-4">
                                    <div>
                                        <label className="text-xs font-semibold text-slate-500 uppercase">Descrizione</label>
                                        <p className="text-slate-800 mt-1">{detailLog.description}</p>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-xs font-semibold text-slate-500 uppercase">Utente</label>
                                            <p className="text-slate-800 mt-1">{getUserFullName(detailLog.user)}</p>
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-slate-500 uppercase">Data</label>
                                            <p className="text-slate-800 mt-1">
                                                {detailLog.created_at && new Date(detailLog.created_at).toLocaleString('it-IT')}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-xs font-semibold text-slate-500 uppercase">Categoria</label>
                                            <p className="text-slate-800 mt-1">{detailLog.action_category}</p>
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-slate-500 uppercase">Azione</label>
                                            <p className="text-slate-800 mt-1">{detailLog.action_type}</p>
                                        </div>
                                    </div>

                                    {detailLog.entity_type && (
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-xs font-semibold text-slate-500 uppercase">Tipo Entità</label>
                                                <p className="text-slate-800 mt-1">{detailLog.entity_type}</p>
                                            </div>
                                            <div>
                                                <label className="text-xs font-semibold text-slate-500 uppercase">ID Entità</label>
                                                <p className="text-slate-800 mt-1 font-mono text-xs break-all">
                                                    {detailLog.entity_id || '-'}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {detailLog.ip_address && (
                                        <div>
                                            <label className="text-xs font-semibold text-slate-500 uppercase">IP Address</label>
                                            <p className="text-slate-800 mt-1 font-mono">{detailLog.ip_address}</p>
                                        </div>
                                    )}

                                    {detailLog.metadata && Object.keys(detailLog.metadata).length > 0 && (
                                        <div>
                                            <label className="text-xs font-semibold text-slate-500 uppercase">Metadata</label>
                                            <pre className="text-xs bg-slate-50 p-3 rounded mt-1 overflow-x-auto">
                                                {JSON.stringify(detailLog.metadata, null, 2)}
                                            </pre>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="p-4 border-t border-slate-200">
                                <button
                                    onClick={() => setShowDetailModal(false)}
                                    className="w-full px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors"
                                >
                                    Chiudi
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                                <h2 className="text-lg font-semibold">Conferma Eliminazione</h2>
                                <button
                                    onClick={() => setShowDeleteConfirm(false)}
                                    className="p-1 hover:bg-slate-100 rounded"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="p-6">
                                <p className="text-slate-700">
                                    Sei sicuro di voler eliminare{' '}
                                    <span className="font-semibold">{selectedLogs.size}</span>{' '}
                                    {selectedLogs.size === 1 ? 'log' : 'log'}?
                                </p>
                                <p className="text-sm text-slate-500 mt-2">
                                    Questa azione è irreversibile.
                                </p>
                            </div>

                            <div className="p-6 border-t border-slate-200 flex gap-3">
                                <button
                                    onClick={() => setShowDeleteConfirm(false)}
                                    className="flex-1 px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors"
                                >
                                    Annulla
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={deleteLogs.isPending}
                                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white rounded-lg transition-colors"
                                >
                                    {deleteLogs.isPending ? 'Eliminazione...' : 'Elimina'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
